/* Lightweight client-side city state + geolocation */
import { geoReverse } from './api';

export const STORAGE_KEY = 'puvio:city';

export const defaultCity = {
  name: 'Paris',
  country: 'France',
  country_code: 'FR',
  lat: 48.8566,
  lon: 2.3522,
  label: 'Paris, France',
};

export function loadCity() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (typeof obj?.lat === 'number' && typeof obj?.lon === 'number') return obj;
    return null;
  } catch { return null; }
}

export function saveCity(city) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(city)); } catch {}
}

/**
 * Detect device location with resilient error handling.
 *
 * - Resolves with a usable city object even when the reverse-geocoding step
 *   fails (we keep raw coords + "Your location" as a fallback name).
 * - Rejects with a typed error so the UI can show the right message.
 *   error.code: 'unsupported' | 'denied' | 'unavailable' | 'timeout' | 'unknown'
 * - Detects iframe permissions-policy issues (Emergent preview) and surfaces
 *   them with a helpful message.
 */
export function detectGeolocation({ timeout = 15000 } = {}) {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      const err = new Error('Geolocation API not available in this browser.');
      err.code = 'unsupported';
      reject(err);
      return;
    }

    // Detect iframe + missing permissions-policy (common in dev preview wrappers)
    let inIframe = false;
    try { inIframe = window.self !== window.top; } catch { inIframe = true; }

    let settled = false;
    const safeResolve = (v) => { if (!settled) { settled = true; resolve(v); } };
    const safeReject = (e) => { if (!settled) { settled = true; reject(e); } };

    // Hard backstop: if the browser silently never invokes either callback
    // (which happens inside iframes lacking allow="geolocation"), fail fast
    // with a clear iframe-permissions error.
    const watchdog = setTimeout(() => {
      const err = new Error(
        inIframe
          ? 'Location request was blocked by the page wrapper. Open the app in your browser tab and try again.'
          : 'Location request timed out. Try again with a stronger signal.'
      );
      err.code = inIframe ? 'iframe_blocked' : 'timeout';
      safeReject(err);
    }, timeout + 2000);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        clearTimeout(watchdog);
        const { latitude, longitude } = pos.coords;
        // Always resolve with valid coords, even if reverse-geocoding fails.
        const fallback = {
          name: 'Your location',
          country: '',
          country_code: '',
          lat: latitude,
          lon: longitude,
          label: `Your location (${latitude.toFixed(3)}, ${longitude.toFixed(3)})`,
        };
        try {
          const rev = await geoReverse(latitude, longitude);
          safeResolve({
            name: rev?.name || fallback.name,
            country: rev?.country || '',
            country_code: rev?.country_code || '',
            lat: latitude,
            lon: longitude,
            label: rev?.label || fallback.label,
          });
        } catch (e) {
          // Network blip / rate-limit on Nominatim — keep coords, lose the city name.
          // eslint-disable-next-line no-console
          console.warn('reverse-geocode failed; using coordinate fallback', e);
          safeResolve(fallback);
        }
      },
      (err) => {
        clearTimeout(watchdog);
        const out = new Error(err?.message || 'Location error');
        // Web Geolocation API codes: 1=PERMISSION_DENIED 2=POSITION_UNAVAILABLE 3=TIMEOUT
        switch (err?.code) {
          case 1: out.code = 'denied'; out.message = 'Location permission denied. Enable it in your browser settings and try again.'; break;
          case 2: out.code = 'unavailable'; out.message = 'Your location could not be determined right now.'; break;
          case 3: out.code = 'timeout'; out.message = 'Location request timed out. Please try again.'; break;
          default: out.code = 'unknown';
        }
        if (inIframe && out.code === 'denied') {
          out.message = 'Location was blocked by the page wrapper. Open the app in a fresh browser tab to allow location.';
          out.code = 'iframe_blocked';
        }
        safeReject(out);
      },
      // Two-stage strategy: we accept a slightly stale fix to make first
      // detection faster. If you want a fresh fix, hit "Refresh" on Home.
      { enableHighAccuracy: false, timeout, maximumAge: 10 * 60 * 1000 }
    );
  });
}
